import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogIn, Lock, Mail, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function StaffLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/staff" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/staff");
    else setError(res.error);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-12" data-testid="staff-login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-[2rem] border border-[#F0E5DE] p-8 lg:p-10 shadow-xl">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-11 h-11 rounded-xl bg-[#FCE7EB] flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#E2718A]" />
            </div>
            <div>
              <h1 className="font-display text-2xl text-[#1F2A2E]">Staff sign in</h1>
              <div className="text-xs uppercase tracking-wider text-[#6B7A80]">Navica CRM</div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#284952] mb-2">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7A80]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="input-navica pl-10"
                  placeholder="you@navicasupports.com"
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#284952] mb-2">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7A80]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="input-navica pl-10"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700" data-testid="login-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="login-submit">
              {submitting ? "Signing in..." : "Sign in"} <LogIn className="w-4 h-4" />
            </button>
          </form>

          <p className="text-xs text-[#6B7A80] mt-7 leading-relaxed">
            Staff access only. If you've forgotten your password, contact the Director to have it reset.
          </p>
        </div>
        <div className="text-center mt-5 text-xs text-[#6B7A80]">
          <a href="/" className="hover:text-[#E2718A] transition">← Back to navicasupports.com</a>
        </div>
      </div>
    </div>
  );
}
